import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// Custom hook to detect mobile view (using 1024px as breakpoint for lg)
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const Sidebar = () => {
  const isMobile = useIsMobile();
  const location = useLocation();

  // Open marketing dropdown by default if the route starts with /Marketing
  const [marketingOpen, setMarketingOpen] = useState(
    location.pathname.startsWith('/Marketing')
  );

  const [toolsOpen, setToolsOpen] = useState(
    location.pathname.startsWith('/tools')
  );

  // Extra props to add only on mobile view
  const mobileProps = isMobile
    ? {
      'data-drawer-target': 'sidebar',
      'data-drawer-toggle': 'sidebar',
      'aria-controls': 'sidebar',
    }
    : {};

  // Base classes for nav links
  const linkClasses = ({ isActive }) => {
    const baseClasses =
      'flex items-center p-3 rounded-lg transition-colors duration-200 pl-8';
    const inactiveClasses =
      'text-[#141718] hover:bg-[rgba(188,0,135,0.07)] hover:text-[#3959ff]';
    const activeClasses = 'bg-[#ffffff] text-[#3959ff] shadow-sm';
    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
  };

  // Determine if current route is under Marketing
  const inMarketing = location.pathname.startsWith('/Marketing');
  const inDashboard = location.pathname.startsWith('/dashboard');
  const inTools = location.pathname.startsWith('/tools');

  return (
    <aside
      id="sidebar"
      className={`fixed top-0 left-0 z-[35] w-64 h-screen pt-20 transition-transform bg-[#f4f6f7] border-r border-[rgba(15,100,146,0.2)] ${isMobile ? '-translate-x-full' : 'translate-x-0'
        }`}
      aria-label="Sidebar"
    >
      <div className="h-full px-3 pb-4 overflow-y-auto">
        <ul className="space-y-2 font-medium">
          {/* Dashboard Link */}
          <li>
            <NavLink to="/dashboard" {...mobileProps} className={`w-full flex items-center p-3 rounded-lg transition-colors duration-200 ${inDashboard
              ? 'bg-[#ffffff] text-[#3959ff] shadow-md'
              : 'text-[#141718] hover:bg-[rgba(188,0,135,0.07)] hover:text-[#3959ff]'
              }`}>
              <svg className="flex-shrink-0 w-6 h-6 mr-3"
                xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill='currentColor'><path d="M4 13h6a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1zm-1 7a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v4zm10 0a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v7zm1-10h6a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1z"></path></svg>
              <span>Dashboard</span>
            </NavLink>
          </li>

          {/* Marketing Dropdown */}
          <li>
            <button
              type="button"
              onClick={() => setMarketingOpen(!marketingOpen)}
              className={`w-full flex items-center p-3 rounded-lg transition-colors duration-200 ${inMarketing
                ? 'bg-[#ffffff] text-[#3959ff] shadow-md'
                : 'text-[#141718] hover:bg-[rgba(188,0,135,0.07)] hover:text-[#3959ff]'
                }`}
            >
              <svg
                className="flex-shrink-0 w-6 h-6 mr-3"
                xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M720-440v-80h160v80H720Zm48 280-128-96 48-64 128 96-48 64Zm-80-480-48-64 128-96 48 64-128 96ZM200-200v-160h-40q-33 0-56.5-23.5T80-440v-80q0-33 23.5-56.5T160-600h160l200-120v480L320-360h-40v160h-80Zm240-182v-196l-98 58H160v80h182l98 58Zm120 36v-268q27 24 43.5 58.5T620-480q0 41-16.5 75.5T560-346ZM300-480Z" /></svg>
              <span>Marketing CRM</span>
              <svg
                className={`ml-auto flex-shrink-0 w-4 h-4 transition-transform duration-200 ${marketingOpen ? 'rotate-90' : ''
                  }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            {marketingOpen && (
              <ul className="mt-1 space-y-1">
                <li>
                  <NavLink
                    to="/Marketing/Lead"
                    {...mobileProps}
                    className={linkClasses}
                  >
                    <svg
                      className="flex-shrink-0 w-5 h-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm720 0v-120q0-44-24.5-84.5T666-434q51 6 96 20.5t84 35.5q36 20 55 44.5t19 53.5v120H760ZM360-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47Zm400-160q0 66-47 113t-113 47q-11 0-28-2.5t-28-5.5q27-32 41.5-71t14.5-81q0-42-14.5-81T544-792q14-5 28-6.5t28-1.5q66 0 113 47t47 113ZM120-240h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm240-320q33 0 56.5-23.5T440-640q0-33-23.5-56.5T360-720q-33 0-56.5 23.5T280-640q0 33 23.5 56.5T360-560Zm0 320Zm0-400Z" /></svg>
                    <span>Leads</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/Marketing/Templates"
                    {...mobileProps}
                    className={linkClasses}
                  >
                    <svg
                      className="flex-shrink-0 w-5 h-5 mr-2"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9.5547 11.1679C9.09517 10.8616 8.4743 10.9858 8.16795 11.4453C7.8616 11.9048 7.98577 12.5257 8.4453 12.8321L12.3906 15.4622C13.0624 15.9101 13.9376 15.9101 14.6094 15.4622L18.5547 12.8321C19.0142 12.5257 19.1384 11.9048 18.8321 11.4453C18.5257 10.9858 17.9048 10.8616 17.4453 11.1679L13.5 13.7981L9.5547 11.1679Z" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2 8C2 5.79086 3.79086 4 6 4H15C16.9002 4 18.4909 5.32493 18.8987 7.10135C20.6751 7.50915 22 9.09984 22 11V17C22 19.2091 20.2091 21 18 21H9C6.79086 21 5 19.2091 5 17V16.9859C3.31747 16.8307 2 15.4153 2 13.6923V8ZM15 6C15.7403 6 16.3866 6.4022 16.7324 7H9C6.79086 7 5 8.79086 5 11V14.9636C4.42625 14.8252 4 14.3085 4 13.6923V8C4 6.89543 4.89543 6 6 6H15ZM9 9C7.89543 9 7 9.89543 7 11V17C7 18.1046 7.89543 19 9 19H18C19.1046 19 20 18.1046 20 17V11C20 9.89543 19.1046 9 18 9H9Z"
                      />
                    </svg>
                    <span>Templates</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/Marketing/Automations"
                    {...mobileProps}
                    className={linkClasses}
                  >
                    <svg
                      className="flex-shrink-0 w-5 h-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                      <path d="M296-270q-42 35-87.5 32T129-269q-34-28-46.5-73.5T99-436l75-124q-25-22-39.5-53T120-680q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47q-9 0-18-1t-17-3l-77 130q-11 18-7 35.5t17 28.5q13 11 31 12.5t35-12.5l420-361q42-35 88-31.5t80 31.5q34 28 46 73.5T861-524l-75 124q25 22 39.5 53t14.5 67q0 66-47 113t-113 47q-66 0-113-47t-47-113q0-66 47-113t113-47q9 0 17.5 1t16.5 3l78-130q11-18 7-35.5T782-630q-13-11-31-12.5T716-630L296-270Zm-16-330q33 0 56.5-23.5T360-680q0-33-23.5-56.5T280-760q-33 0-56.5 23.5T200-680q0 33 23.5 56.5T280-600Zm400 400q33 0 56.5-23.5T760-280q0-33-23.5-56.5T680-360q-33 0-56.5 23.5T600-280q0 33 23.5 56.5T680-200ZM280-680Zm400 400Z" /></svg>
                    <span>Automations</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/Marketing/Report"
                    {...mobileProps}
                    className={linkClasses}
                  >
                    <svg
                      className="flex-shrink-0 w-5 h-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M120-120v-80l80-80v160h-80Zm160 0v-240l80-80v320h-80Zm160 0v-320l80 81v239h-80Zm160 0v-239l80-80v319h-80Zm160 0v-400l80-80v480h-80ZM120-327v-113l280-280 160 160 280-280v113L560-447 400-607 120-327Z" /></svg>
                    <span>Reports</span>
                  </NavLink>
                </li>
              </ul>
            )}
          </li>
          {/* Tools Dropdown */}
          <li>
            <button
              type="button"
              onClick={() => setToolsOpen(!toolsOpen)}
              className={`w-full flex items-center p-3 rounded-lg transition-colors duration-200 ${inTools
                ? 'bg-[#ffffff] text-[#3959ff] shadow-md'
                : 'text-[#141718] hover:bg-[rgba(188,0,135,0.07)] hover:text-[#3959ff]'
                }`}
            >
              <svg
                className="flex-shrink-0 w-6 h-6 mr-3"
                viewBox="0 -960 960 960" fill="currentColor"><path d="M666-440 440-666l226-226 226 226-226 226Zm-546-80v-320h320v320H120Zm400 400v-320h320v320H520Zm-400 0v-320h320v320H120Zm80-480h160v-160H200v160Zm467 48 113-113-113-113-113 113 113 113Zm-67 352h160v-160H600v160Zm-400 0h160v-160H200v160Zm160-400Zm194-65ZM360-360Zm240 0Z" /></svg>
              <span>Tools</span>
              <svg
                className={`ml-auto flex-shrink-0 w-4 h-4 transition-transform duration-200 ${toolsOpen ? 'rotate-90' : ''
                  }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            {toolsOpen && (
              <ul className="mt-1 space-y-1">
                <li>
                  <NavLink
                    to="/tools/email-verify"
                    {...mobileProps}
                    className={linkClasses}
                  >
                    <svg
                      className="flex-shrink-0 w-5 h-5 mr-2" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="m612-550 141-142-28-28-113 113-57-57-28 29 85 85ZM120-160v-80h480v80H120Zm520-280q-83 0-141.5-58.5T440-640q0-83 58.5-141.5T640-840q83 0 141.5 58.5T840-640q0 83-58.5 141.5T640-440Zm-520-40v-80h252q7 22 16 42t22 38H120Zm0 160v-80h376q23 14 49 23.5t55 13.5v43H120Z" /></svg>
                    <span>Email Validator</span>
                  </NavLink>
                </li>
              </ul>
            )}
          </li>

          {/* Logout */}
          <li>
            <NavLink
              to="/logout"
              className={({ isActive }) =>
                'flex items-center p-3 rounded-lg transition-colors duration-200 text-[#141718] hover:bg-[rgba(188,0,135,0.07)] hover:text-[#3959ff]'
              }
            >
              <svg
                className="flex-shrink-0 w-6 h-6 mr-3"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M16 13v-2H7V8l-5 4 5 4v-3z" />
                <path d="M20 3h-9c-1.103 0-2 .897-2 2v4h2V5h9v14h-9v-4H9v4c0 1.103.897 2 2 2h9c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2z" />
              </svg>
              <span>Logout</span>
            </NavLink>
          </li>
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
